import isEqual from 'lodash/isEqual';
import { type ReactNode, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { CowWaveLoader } from '../components/CowWaveLoader';
import { WORKSPACE_TYPE_ROUTES } from '../helpers';
import { useCurrentWorkspace } from '../hooks/useCurrentWorkspace';
import { useFilterConfigs } from '../hooks/useFilterConfigs';
import { useWorkspaceNavigate } from '../hooks/useWorkspaceNavigate';
import { useSingleWorkspace } from '../hooks/useWorkspaces/useSingleWorkspace';
import { workspaceTypeChecks } from '../lib/api/helpers';
import { useWorkspaceFilters } from '../modules/GenomeWorkspaceView/hooks/useWorkspaceFilters';
import { useAdvertisers } from '../modules/GenomeWorkspaceView/LeftView/hooks/useAdvertisers';
import { useUserData } from '../modules/GenomeWorkspaceView/LeftView/hooks/useUserData';

interface WorkspaceDataProviderProps {
  children: ReactNode;
  workspaceId: number;
  checkTemplateData?: boolean;
}

export const WorkspaceDataProvider = ({ workspaceId, children, checkTemplateData }: WorkspaceDataProviderProps) => {
  const { userData } = useUserData('acctId');
  const acctIdNumber = Number(userData.acctId);
  const { loadFiltersForWorkspace } = useWorkspaceFilters();
  const { currentWorkspace, setCurrentWorkspace } = useCurrentWorkspace();
  const { navigateToWorkspaceList } = useWorkspaceNavigate();
  const { isAdvertisersDataLoading } = useAdvertisers();
  const location = useLocation();

  const v7WorkspaceTypeID = useMemo(() => {
    const typeID = Object.entries(WORKSPACE_TYPE_ROUTES).find(([, path]) =>
      location.pathname.includes(`${path}/`),
    )?.[0];

    if (!typeID || !workspaceTypeChecks.isV7Workspace(Number(typeID))) {
      return undefined;
    }

    return Number(typeID);
  }, [location.pathname]);

  const { workspace, isWorkspaceLoading, isWorkspaceRefetching } = useSingleWorkspace({
    accountID: acctIdNumber,
    workspaceID: workspaceId,
    // TODO: Remove workspaceTypeID when all types are migrated to V7
    workspaceTypeID: v7WorkspaceTypeID,
  });

  const {
    isLoading: isConfigLoading,
    isFetching: isConfigFetching,
    isError: isConfigError,
    filterConfigs,
  } = useFilterConfigs({
    disableAutoFetch: !workspace?.workspace_type_id,
    workspaceTypeId: workspace?.workspace_type_id,
  });

  const isLoading = useMemo(() => {
    const isFilterConfigLoading = isConfigLoading || (isConfigFetching && isConfigError);

    return isWorkspaceLoading || isFilterConfigLoading || isAdvertisersDataLoading;
  }, [isConfigError, isAdvertisersDataLoading, isConfigLoading, isConfigFetching, isWorkspaceLoading]);

  useEffect(() => {
    if (isLoading || isWorkspaceRefetching) {
      return;
    }

    if (checkTemplateData && workspace && !workspace.workspace_looker_dashboard_details) {
      console.error('missing workspace template');
      navigateToWorkspaceList();
    }

    if (filterConfigs && workspace) {
      const hasWorkspaceChanged = currentWorkspace?.workspace_id !== workspace.workspace_id;
      const hasNoServerChanges =
        workspace.workspace_updated_at == null ||
        currentWorkspace?.workspace_updated_at === workspace.workspace_updated_at;
      const hasBeenEdited = !isEqual(currentWorkspace, workspace);
      const keepDraft = !hasWorkspaceChanged && hasNoServerChanges && hasBeenEdited;

      if (hasWorkspaceChanged) {
        loadFiltersForWorkspace(workspace, workspace.workspace_looker_dashboard_details ?? {}, filterConfigs || []);
      }

      if (!keepDraft) {
        setCurrentWorkspace(workspace);
      }
    } else {
      navigateToWorkspaceList();
    }
  }, [filterConfigs, workspace, isLoading, isWorkspaceRefetching]);

  if (isLoading || !currentWorkspace) {
    return <CowWaveLoader centered />;
  }

  return <>{children}</>;
};

export default WorkspaceDataProvider;
